import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
} from "@mui/material";
import useTranslation from "shared/helpers/hooks/useTranslation";

interface DynamicDataTableProps {
  data: any[];
  title?: string;
}

const DynamicDataTable: React.FC<DynamicDataTableProps> = ({
  data,
  title = "AI Assistant Results",
}) => {
  const t = useTranslation();

  // Generate columns dynamically based on the data structure
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    return Object.keys(firstRow).map((key) => ({
      label: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
      field: key,
    }));
  }, [data]);

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined) return "N/A";

    // Format dates
    if (
      typeof value === "string" &&
      value.includes("T") &&
      value.includes(":")
    ) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        }
      } catch (e) {
        // If date parsing fails, return original value
      }
    }

    // Format numbers with decimals
    if (typeof value === "number") {
      if (Number.isInteger(value)) {
        return value.toString();
      } else {
        return value.toFixed(2);
      }
    }

    return value.toString();
  };

  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const ITEMS_PER_PAGE = 50;

  // Simple pagination logic
  const visibleData = useMemo(() => {
    return data.slice(visibleRange.start, visibleRange.end);
  }, [data, visibleRange]);

  const handleLoadMore = () => {
    setVisibleRange((prev) => ({
      start: prev.start,
      end: Math.min(prev.end + ITEMS_PER_PAGE, data.length),
    }));
  };

  const handleReset = () => {
    setVisibleRange({ start: 0, end: ITEMS_PER_PAGE });
  };

  if (!data || data.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          {t("no_data_available")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing {visibleRange.start + 1}-
          {Math.min(visibleRange.end, data.length)} of {data.length} records
        </Typography>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{
                      backgroundColor: "#f5f5f5",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleData.map((rowData, index) => (
                <TableRow key={visibleRange.start + index} hover>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      sx={{ borderBottom: "1px solid #e0e0e0" }}
                    >
                      <Typography variant="body2">
                        {formatCellValue(rowData[column.field || ""])}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {data.length > ITEMS_PER_PAGE && (
          <Box display="flex" justifyContent="center" p={2} gap={2}>
            {visibleRange.end < data.length && (
              <Button variant="outlined" onClick={handleLoadMore}>
                Load More (
                {Math.min(ITEMS_PER_PAGE, data.length - visibleRange.end)} more)
              </Button>
            )}
            {visibleRange.end > ITEMS_PER_PAGE && (
              <Button variant="text" onClick={handleReset}>
                Reset
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DynamicDataTable;
